import ConfirmModal from 'components/ConfirmModal'
import Details from 'components/Details'
import { IconCheck } from 'components/form'
import I from 'components/I'
import Tip from 'components/Tip'
import { mapValues } from 'lodash-es'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { Form } from 'react-bootstrap'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { getCannedMessages } from 'state/Account'
import { notifyCustomer } from 'state/Orders'
import { Alerts } from 'utils'

export default function NotifyModal({ show, doClose, isCancelled }) {
	const [initial, options] = NotifyModal.useOptions()

	const { t }        = useTranslation()
	const { id }       = useParams()
	const dispatch     = useDispatch()
	const msgState     = useState()
	const mediumsState = useState(initial)

	// t('Order is ready')
	// t("Driver can't reach you")
	// t('Closed, order canceled')
	// t('Card declined')
	const cannedMsgs = useSelector(getCannedMessages)
	const msgOptions = mapValues(cannedMsgs, ({ icon, title }) => {
		let content = t(title)
		if (title.toLowerCase().includes('cancel')) {
			content = <>
				{t(title)} {/* negative top margin to compensate height; left to compensate inner default px-2 */}
				<Tip.Bubble sm className="mt-n1 ml-n1">
						<p><Trans>This <b>won't</b> alter the order, it's just a notification.</Trans></p>
						{!isCancelled && <p>{t('Remember to cancel the order from the top-right menu.')}</p>}
				</Tip.Bubble>
			</>
		}
		return [icon, content]
	})
	const readyToSend = mediumsState[0] && msgState[0]

	function beforeClose() {
		msgState[1]()
		mediumsState[1](initial)
	}

	async function doSubmit() {
		await dispatch(notifyCustomer(id, mediumsState[0], msgState[0]))
			.then(() => Alerts.success(t('The message has been sent.')))
			.finally(doClose)
	}

	return <ConfirmModal {...{ show, beforeClose, doClose, doSubmit }}
	                     confirmTexts={[t('Send'), t('Sending')]}
	                     confirmIcon="paper-plane"
	                     title={t('Message customer')}
	                     disabled={!readyToSend}>

		<Form.Group controlId="msg">
			<Form.Label>{t('Which message?')}</Form.Label>
			<IconCheck.Group radio statePair={msgState} initial={msgState[0]} options={msgOptions}/>
		</Form.Group>

		<Form.Group>
			<Form.Label>{t('How?')}</Form.Label>
			<IconCheck.Group radio statePair={mediumsState} {...{ options, initial }}/>
		</Form.Group>

		{readyToSend && <Details actionTxts={[t('Show message'), t('Hide message')]}>
			<small className="d-block p-1" style={{ border: '1px dashed darkgray' }}>
				<I force className="d-block mb-2">
					{mediumsState[0] != 'sms' && <><b>{t('Subject:')}</b> {cannedMsgs[msgState[0]].subject}<br/></>}
					{mediumsState[0] != 'sms' && <b>{t('Body:', { context: 'of text' })}</b>} {cannedMsgs[msgState[0]].body}
				</I>
				<span className="d-block m-n1 p-1" style={{ backgroundColor: 'gainsboro' }}>
					<Trans context="keywords are 'macros' in messages sent to customers">These <tt>{'{{KEYWORDS}}'}</tt> will be replaced.</Trans>
				</span>
			</small>
		</Details>}
	</ConfirmModal>
}

/**
 * Returns the usually initial value for quick customer notifications, and the other options (with icon and text), indexed by the value.
 * @returns {['sms',{sms: [string,string], email: [string,string], both: [string,string]}]}
 */
NotifyModal.useOptions = () => {
	const { t } = useTranslation()
	return [
		'sms', //initial
		{ //options
			email: ['envelope', t('Email')],
			sms  : ['sms', t('SMS')],
			both : ['asterisk', t('Both')],
		}
	]
}

NotifyModal.propTypes = {
	show       : PropTypes.bool.isRequired,
	doClose    : PropTypes.func.isRequired,
	isCancelled: PropTypes.bool.isRequired,
}
